import { useState, useEffect } from 'react';
import { MapPin, ChevronDown, X, Plus } from 'lucide-react';
import type { SprayTargetInput, TargetType, OrchardTargetType } from '../../types/spray';
import orchardService from '../../services/orchard';

interface SprayTargetSelectorProps {
  value: SprayTargetInput[];
  onChange: (targets: SprayTargetInput[]) => void;
  error?: string;
  /** When provided, filters blocks and cultivar areas to this farm only */
  farmId?: number;
}

interface TargetOption {
  type: TargetType;
  id: number;
  name: string;
  area?: number;
  parentName?: string;
}

function SprayTargetSelector({ value, onChange, error, farmId }: SprayTargetSelectorProps) {
  const [farms, setFarms] = useState<any[]>([]);
  const [blocks, setBlocks] = useState<any[]>([]);
  const [cultivarAreas, setCultivarAreas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState<OrchardTargetType>('cultivar_area');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const loadOptions = async () => {
      setLoading(true);
      try {
        const [farmsRes, blocksRes, areasRes] = await Promise.all([
          orchardService.getAllFarms(),
          orchardService.getAllBlocks(),
          orchardService.getAllCultivarAreas(),
        ]);
        if (farmsRes.success)  setFarms((farmsRes.data as any)?.farms || []);
        if (blocksRes.success) setBlocks((blocksRes.data as any)?.blocks || []);
        if (areasRes.success)  setCultivarAreas((areasRes.data as any)?.areas || []);
      } catch {
        // ignore
      } finally {
        setLoading(false);
      }
    };
    loadOptions();
  }, []);

  // If farmId filtering changes and the current activeType is 'farm', reset to 'cultivar_area'
  useEffect(() => {
    if (farmId !== undefined && activeType === 'farm') setActiveType('cultivar_area');
  }, [farmId, activeType]);

  const farmFiltered = farmId !== undefined;

  const filteredBlocks = farmFiltered
    ? blocks.filter((b: any) => b.farm_id === farmId)
    : blocks;

  const filteredAreas = farmFiltered
    ? cultivarAreas.filter((a: any) => a.farm_id === farmId)
    : cultivarAreas;

  const options: TargetOption[] = [
    ...(!farmFiltered ? farms.map((f: any) => ({
      type: 'farm' as TargetType,
      id: f.id,
      name: f.name,
      area: f.total_area_hectares,
    })) : []),
    ...filteredBlocks.map((b: any) => ({
      type: 'block' as TargetType,
      id: b.id,
      name: b.name,
      area: b.area_hectares,
      parentName: b.farm_name,
    })),
    ...filteredAreas.map((a: any) => ({
      type: 'cultivar_area' as TargetType,
      id: a.id,
      name: a.name,
      area: a.area_hectares,
      parentName: a.block_name || a.farm_name,
    })),
  ];

  const availableTabs: OrchardTargetType[] = farmFiltered
    ? ['cultivar_area', 'block']
    : ['cultivar_area', 'block', 'farm'];

  const filtered = options.filter(o => o.type === activeType);
  const isSelected = (type: TargetType, id: number) =>
    value.some(t => t.target_type === type && t.target_id === id);

  const toggle = (option: TargetOption) => {
    const already = isSelected(option.type, option.id);
    if (already) {
      onChange(value.filter(t => !(t.target_type === option.type && t.target_id === option.id)));
    } else {
      onChange([...value, { target_type: option.type, target_id: option.id }]);
    }
  };

  const remove = (target: SprayTargetInput) => {
    onChange(value.filter(t => !(t.target_type === target.target_type && t.target_id === target.target_id)));
  };

  const selectedOptions = value.map(t => {
    const opt = options.find(o => o.type === t.target_type && o.id === t.target_id);
    return opt || { type: t.target_type, id: t.target_id, name: `${t.target_type} #${t.target_id}` };
  });

  const totalArea = selectedOptions.reduce((sum, o) => sum + (parseFloat(String(o.area)) || 0), 0);

  const typeLabels: Record<OrchardTargetType, string> = {
    farm: 'Farms',
    block: 'Blocks',
    cultivar_area: 'Cultivar Areas',
  };

  return (
    <div className="space-y-2">
      {/* Selected targets */}
      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2 p-3 bg-neutral-50 rounded-lg border border-neutral-200">
          {selectedOptions.map(opt => (
            <span
              key={`${opt.type}-${opt.id}`}
              className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 rounded-full px-3 py-1 text-sm"
            >
              <MapPin className="h-3.5 w-3.5 text-green-600" />
              {opt.name}
              <span className="text-neutral-400 text-xs capitalize">({opt.type.replace('_', ' ')})</span>
              <button
                onClick={() => remove({ target_type: opt.type, target_id: opt.id })}
                className="text-neutral-400 hover:text-red-500 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
          <p className="w-full text-xs text-neutral-500 mt-1">
            Total area: <strong>{totalArea.toFixed(2)} ha</strong>
          </p>
        </div>
      )}

      {/* Add targets button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`w-full flex items-center justify-between px-4 py-3 border rounded-lg text-sm transition-colors ${
          error
            ? 'border-red-300 bg-red-50'
            : 'border-neutral-300 hover:border-neutral-400 bg-white'
        }`}
      >
        <span className="flex items-center gap-2 text-neutral-600">
          <Plus className="h-4 w-4" />
          Add spray targets
        </span>
        <ChevronDown className={`h-4 w-4 text-neutral-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {error && <p className="text-xs text-red-500">{error}</p>}

      {/* Dropdown panel */}
      {open && (
        <div className="border border-neutral-200 rounded-lg bg-white shadow-lg overflow-hidden">
          {/* Type tabs */}
          <div className="flex border-b border-neutral-200">
            {availableTabs.map(type => (
              <button
                key={type}
                type="button"
                onClick={() => setActiveType(type)}
                className={`flex-1 py-2 text-xs font-medium transition-colors ${
                  activeType === type
                    ? 'bg-primary-50 text-primary-700 border-b-2 border-primary-500'
                    : 'text-neutral-500 hover:text-neutral-700'
                }`}
              >
                {typeLabels[type]}
              </button>
            ))}
          </div>

          {/* Options list */}
          <div className="max-h-48 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-sm text-neutral-500">Loading...</div>
            ) : filtered.length === 0 ? (
              <div className="p-4 text-center text-sm text-neutral-500">
                No {typeLabels[activeType].toLowerCase()} found
              </div>
            ) : (
              filtered.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => toggle(opt)}
                  className={`w-full flex items-center justify-between px-4 py-2.5 text-sm text-left hover:bg-neutral-50 transition-colors ${
                    isSelected(opt.type, opt.id) ? 'bg-primary-50' : ''
                  }`}
                >
                  <div>
                    <span className="font-medium text-neutral-800">{opt.name}</span>
                    {opt.parentName && (
                      <span className="text-neutral-400 text-xs ml-2">{opt.parentName}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {opt.area != null && (
                      <span className="text-xs text-neutral-400">{Number(opt.area).toFixed(1)} ha</span>
                    )}
                    {isSelected(opt.type, opt.id) && (
                      <span className="h-4 w-4 rounded-full bg-primary-500 flex items-center justify-center">
                        <span className="text-white text-xs">✓</span>
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SprayTargetSelector;
